import { useEffect, useState } from 'react'
import { documentTypeService } from '../services/document-type.service'
import { PermissionAction, permissionsService, PermissionType } from '../services/permissions.service'
import { DocumentType } from '../types'

let globalCache: DocumentType[] | null = null
let globalCachePromise: Promise<DocumentType[]> | null = null
let cacheListeners: Set<() => void> = new Set()

const loadCache = async (): Promise<DocumentType[]> => {
  if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.DocumentType)) {
    return []
  }
  if (globalCache) {
    return globalCache
  }

  if (globalCachePromise) {
    return globalCachePromise
  }

  globalCachePromise = documentTypeService
    .listAll()
    .then((response) => {
      globalCache = response.results || []
      globalCachePromise = null
      cacheListeners.forEach((listener) => listener())
      return globalCache
    })
    .catch(() => {
      globalCachePromise = null
      return []
    })

  return globalCachePromise
}

export const useDocumentTypesCache = (): { documentTypes: DocumentType[]; loaded: boolean } => {
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>(globalCache || [])
  const [loaded, setLoaded] = useState(!!globalCache)

  useEffect(() => {
    if (globalCache) {
      setDocumentTypes(globalCache)
      setLoaded(true)
      return
    }

    const listener = () => {
      if (globalCache) {
        setDocumentTypes(globalCache)
        setLoaded(true)
      }
    }

    cacheListeners.add(listener)

    loadCache().then((cached) => {
      setDocumentTypes(cached)
      setLoaded(true)
    })

    return () => {
      cacheListeners.delete(listener)
    }
  }, [])

  return { documentTypes, loaded }
}
